import { lazy, Suspense, useMemo } from "react";
import ComingSoonPage from "@/components/ComingSoonPage";
import NotFoundPage from "@/components/NotFoundPage";
import UpgradePrompt from "@/components/UpgradePrompt";
import { ACCESS_TIERS } from "@/config/routeAccess";
import { useEntitlements } from "@/context/EntitlementsContext";

function AccessLoading() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center text-sm text-muted-foreground">
      Checking access...
    </div>
  );
}

function AccessCheckFailed({ onRetry }) {
  return (
    <div className="min-h-[50vh] flex flex-col items-center justify-center gap-2 text-sm">
      <p className="text-muted-foreground">We couldn't check your access just now.</p>
      <button
        type="button"
        onClick={() => onRetry?.()}
        className="h-8 px-3 rounded-md border border-border text-xs hover:bg-muted"
      >
        Retry
      </button>
    </div>
  );
}

export default function RouteGuard({ tier, children, loadPrivate }) {
  const entitlements = useEntitlements();
  const PrivateComponent = useMemo(
    () => (loadPrivate ? lazy(loadPrivate) : null),
    [loadPrivate]
  );

  if (tier === ACCESS_TIERS.FREE) return children;
  if (tier === ACCESS_TIERS.NOT_FOUND) return <NotFoundPage />;
  if (tier === ACCESS_TIERS.COMING_SOON) return <ComingSoonPage />;

  if (tier === ACCESS_TIERS.CORE) {
    if (entitlements.loading) return <AccessLoading />;
    if (entitlements.isResearchPlus) return children;
    // A failed check is not a confirmed free plan: offer a retry, not the upgrade lock.
    if (entitlements.error) return <AccessCheckFailed onRetry={entitlements.refreshEntitlements} />;
    return <UpgradePrompt />;
  }

  if (tier === ACCESS_TIERS.OWNER) {
    if (entitlements.loading) return <AccessLoading />;
    if (!entitlements.is_owner) return <NotFoundPage />;
    if (PrivateComponent) {
      return (
        <Suspense fallback={<AccessLoading />}>
          <PrivateComponent />
        </Suspense>
      );
    }
    return children || <NotFoundPage />;
  }

  return <NotFoundPage />;
}

